import type { StreamFailure } from "@shared/player/streamPlayback";

/**
 * What the player saw, counted (PLAYER-17).
 *
 * **Content playback telemetry does not exist anywhere in the platform today.**
 * The vendor streaming app's only route is a fixed `/eventsink/*` stub that
 * answers `{status:"received"}` and stores nothing, so owning the player is the
 * one chance to fix that - and this is the seam ANALYTICS-1 and ANALYTICS-2 both
 * point at.
 *
 * ## Counts, never a trail
 *
 * This is the privacy fork ANALYTICS-1 opened, taken deliberately. Nothing here
 * records WHAT was watched, WHEN, or by whom: it is a tally of plays, failures
 * by named code, and rebuffers, reset when the day rolls over. A room's viewing
 * history is not ours to keep, a care premise raises that bar again, and nothing
 * that cannot survive a check-out should exist to survive it.
 *
 * The consequence is worth stating plainly: **this can answer "is playback
 * broken here" and can never answer "what did room 302 watch".** The first is an
 * operations question that currently has no answer at all. The second is one we
 * have chosen not to be able to answer.
 *
 * ## Why it rides the heartbeat
 *
 * A heartbeat already leaves every device every 30 seconds, already carries the
 * signage playback report, and is already authenticated by screen token. A
 * second endpoint would be a second thing to authenticate, rate-limit and drop
 * on the floor when a panel is offline - for a payload of six integers.
 */

export interface PlaybackTally {
  /** Loads attempted since the day began. */
  started: number;
  /** Loads that reached first frame. `started - playing` is the silent gap. */
  playing: number;
  /** Rebuffers observed mid-play. */
  rebuffers: number;
  /** Completed to the end. */
  completed: number;
  /**
   * Failures by their typed code from PLAYER-9.
   *
   * By CODE rather than a single count, because the response differs entirely:
   * `licence_refused` across a whole premise is an entitlement or key problem
   * somebody must act on today, while `network_stalled` on one room at 19:00 is
   * a hotel's uplink at dinner time.
   */
  failures: Partial<Record<StreamFailure, number>>;
  /**
   * Channel changes measured end to end, keypress → first frame (`TV-4`).
   *
   * ⚠️ **This is the number that decides whether guests accept live TV in our
   * own client at all.** A guest compares a zap to a broadcast tuner; MSE plus a
   * DRM round trip is not that, and the whole pre-warm design in
   * `schemes/_shared/guide/zap.ts` exists to close the gap. A design target
   * nobody measures is a wish, so the player states one and counts what it
   * actually did.
   *
   * Total and worst rather than a list of samples: the tally is a handful of
   * integers on a heartbeat and must stay that way (see the header). Mean tells
   * you whether the design works; worst tells you whether one channel in the
   * line-up is dragging.
   *
   * These reach the fleet console: `PlaybackTallyReport` carries them and
   * `sanitizeTally` (`src/server/api/screens.ts`) copies them, capping the two
   * millisecond totals on a 24-hour ceiling rather than the count ceiling.
   */
  zaps: number;
  zapMsTotal: number;
  zapMsWorst: number;
  /** The local date these counts belong to (YYYY-MM-DD), so a stale bucket is
   *  visible rather than silently merged into today. */
  day: string;
}

function emptyTally(day: string): PlaybackTally {
  return {
    started: 0,
    playing: 0,
    rebuffers: 0,
    completed: 0,
    failures: {},
    zaps: 0,
    zapMsTotal: 0,
    zapMsWorst: 0,
    day,
  };
}

function today(now: Date): string {
  // Local date, not UTC: "how did last night go" is asked in the hotel's own
  // day, and a UTC bucket splits a European evening across two of them.
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
}

let tally: PlaybackTally = emptyTally(today(new Date()));

/** Roll the bucket when the day changes. Called before every mutation. */
function current(now = new Date()): PlaybackTally {
  const day = today(now);
  if (tally.day !== day) tally = emptyTally(day);
  return tally;
}

export function playbackStarted(now?: Date): void {
  current(now).started += 1;
}

export function playbackPlaying(now?: Date): void {
  current(now).playing += 1;
}

export function playbackRebuffered(now?: Date): void {
  current(now).rebuffers += 1;
}

export function playbackCompleted(now?: Date): void {
  current(now).completed += 1;
}

export function playbackFailed(failure: StreamFailure, now?: Date): void {
  const t = current(now);
  t.failures[failure] = (t.failures[failure] ?? 0) + 1;
}

/**
 * One channel change, keypress → first frame, in milliseconds (`TV-4`).
 *
 * A change that never reached a frame is NOT counted here: it is a failure and
 * is already counted as one. Folding a timeout in as "12000 ms" would make the
 * mean say the player is slow when what it is, is broken — two different
 * problems with two different owners.
 */
export function playbackZapped(ms: number, now?: Date): void {
  if (!Number.isFinite(ms) || ms < 0) return;
  const t = current(now);
  const rounded = Math.round(ms);
  t.zaps += 1;
  t.zapMsTotal += rounded;
  t.zapMsWorst = Math.max(t.zapMsWorst, rounded);
}

/**
 * What to attach to the next heartbeat, or null when nothing has played.
 *
 * Null rather than a zeroed block: "this screen has played nothing today" and
 * "this screen is not a playback surface at all" are different, and a wall of
 * zeroes across a hotel's door signs would bury the one room that matters.
 */
export function currentPlaybackTally(now?: Date): PlaybackTally | null {
  const t = current(now);
  const touched =
    t.started > 0 ||
    t.playing > 0 ||
    t.rebuffers > 0 ||
    t.completed > 0 ||
    t.zaps > 0 ||
    Object.keys(t.failures).length > 0;
  return touched ? { ...t, failures: { ...t.failures } } : null;
}

/** Test seam, and what a role change away from playback should call. */
export function resetPlaybackTally(now = new Date()): void {
  tally = emptyTally(today(now));
}
